import * as R from "remeda";
import { useEffect, useState } from "react";
import type {
  ConditionFieldDescription,
  InputDescription,
  IntegrationDescription,
  ValueConfig,
} from "@smartsuite/automation-engine-api";
import { NumberEditor } from "../FieldEditor/FieldEditorRow/fields/NumberEditor";
import { SelectEditor } from "../FieldEditor/FieldEditorRow/fields/SelectEditor";
import { StringEditor } from "../FieldEditor/FieldEditorRow/fields/StringEditor";
import { EmailEditor } from "../EmailEditor";
import { MemberEditor } from "../MemberEditor";
import { DateEditor } from "../FieldEditor/FieldEditorRow/fields/DateEditor";
import { LinkEditor } from "../FieldEditor/FieldEditorRow/fields/LinkEditor";
import { PhoneEditor } from "../FieldEditor/FieldEditorRow/fields/PhoneEditor";
import type { CandidateStore } from "../../../../../stores/automation/candidate.store";
import type { FnConfig } from "../../../Sections/TriggerSection/ConditionsForm/ConditionItem/utils/types";
import { isValueNotValid } from "../FieldEditor/FieldEditorRow/fields/utils";
import type { ActionCandidate } from "../../../../../types/candidate";
import type { LoopScope } from "../../../../../stores/automation/looping.store";
import { needShowTeams } from "./needShowTeams";
import { useUpstreamActions } from "../hooks/use-upstream-actions";

const conditionFieldDescriptionToInputDescription = (
  fieldDescription: ConditionFieldDescription,
): InputDescription => ({
  inputId: fieldDescription.conditionFieldId,
  label: fieldDescription.label,
  value: fieldDescription.value,
  editor: fieldDescription.editor,
  icon: fieldDescription.icon,
  groupFieldId: fieldDescription.groupFieldId,
  labelSuffix: fieldDescription.labelSuffix,
  subLabel: "",
  dynamic: false,
});

export const ArgumentEditor: React.FC<{
  inputId?: string;
  value?: ValueConfig;
  fieldDescription: ConditionFieldDescription;
  functionConfig?: FnConfig;
  candidate: CandidateStore;
  integrations: IntegrationDescription[];
  onChange: (value?: ValueConfig) => void;
  action?: ActionCandidate;
  /** Explicit, already-scoped data sources for this argument, used instead of
   * deriving them from `action`. Conditional Logic branch conditions pass the
   * actions upstream of their block: they have no owning action, and scoping by
   * the left side's resolved action offered too little (BUG-21770). */
  sourceActions?: ActionCandidate[];
  /** The loop whose Current Item this argument may offer. Passed alongside
   * `sourceActions` by Conditional Logic branch conditions: it is the loop
   * enclosing their block, and `undefined` means the branch is not in a loop. */
  loopScope?: LoopScope;
}> = ({
  inputId = "",
  value,
  fieldDescription,
  functionConfig,
  candidate,
  integrations,
  onChange,
  action,
  sourceActions,
  loopScope,
}) => {
  const [state, setState] = useState<{ prevFn: string; isReload: boolean }>({
    prevFn: "",
    isReload: false,
  });
  const inputDescription =
    conditionFieldDescriptionToInputDescription(fieldDescription);
  // Data sources offered to this argument: only actions upstream of the
  // current action on its own path -- sibling branch outputs never leak
  // (spec 8.1/8.2). Falls back to the full list for trigger/branch conditions.
  const upstreamActions = useUpstreamActions(candidate, action);
  // A caller-supplied list is authoritative: it is already scoped, so the
  // editors must offer it whole rather than slicing it at `action`.
  const actionsArePreScoped = sourceActions !== undefined;
  const scopedActions = sourceActions ?? upstreamActions;

  useEffect(() => {
    const currFn = functionConfig?.[0];
    if (R.isTruthy(currFn) && currFn !== state.prevFn) {
      setState({ prevFn: currFn, isReload: true });
    } else if (state.isReload) {
      setState((s) => ({ ...s, isReload: false }));
    }
  }, [functionConfig, state]);

  if (R.isNil(functionConfig) || state.isReload) return null;

  switch (functionConfig[3]) {
    case "text":
      return (
        <StringEditor // todo isError
          input={{ inputId, value }}
          onChange={(input) => onChange(input.value)}
          trigger={candidate.self.trigger}
          action={action}
          actions={scopedActions}
          actionsArePreScoped={actionsArePreScoped}
          loopScope={loopScope}
          integrations={integrations}
          description={inputDescription}
          hideCaptionBlock={true}
          showValidationError={false}
          isMultiple={functionConfig[2]}
          isError={action !== undefined && isValueNotValid(candidate, value)}
          disableSplit={true}
        />
      );
    case "date":
      return (
        <DateEditor
          input={{ inputId, value }}
          onChange={(input) => onChange(input.value)}
          trigger={candidate.self.trigger}
          actions={scopedActions}
          actionsArePreScoped={actionsArePreScoped}
          loopScope={loopScope}
          action={action}
          integrations={integrations}
          isError={action !== undefined && isValueNotValid(candidate, value)}
          useDateWithTime={false}
        />
      );
    case "select":
    case "status":
    case "tag":
      return (
        <SelectEditor
          input={{ inputId: fieldDescription.conditionFieldId, value }}
          onChange={(input) => onChange(input.value)}
          trigger={candidate.self.trigger}
          actions={scopedActions}
          actionsArePreScoped={actionsArePreScoped}
          loopScope={loopScope}
          action={action}
          integrations={integrations}
          editor={fieldDescription.editor}
          isMultiple={functionConfig[2]}
          isError={action !== undefined && isValueNotValid(candidate, value)}
          type={
            fieldDescription?.editor?.editor.oneofKind === "select"
              ? fieldDescription.editor.editor.select.type
              : undefined
          }
          disableSplit={true}
        />
      );
    case "member":
      return (
        <MemberEditor
          config={{ inputId, value }}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          trigger={candidate.self.trigger}
          onChange={(input) => onChange(input.value)}
          showValidationError={false}
          actions={scopedActions}
          actionsArePreScoped={actionsArePreScoped}
          loopScope={loopScope}
          action={action}
          integrations={integrations}
          isMultiple={functionConfig[2]}
          isError={action !== undefined && isValueNotValid(candidate, value)}
          disableSplit={true}
        />
      );
    case "email":
      return (
        <EmailEditor
          config={{ inputId, value }}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          editor={fieldDescription.editor!}
          trigger={candidate.self.trigger}
          onChange={(input) => onChange(input.value)}
          showValidationError={false}
          actions={scopedActions}
          actionsArePreScoped={actionsArePreScoped}
          loopScope={loopScope}
          action={action}
          integrations={integrations}
          isMultiple={functionConfig[2]}
          isError={action !== undefined && isValueNotValid(candidate, value)}
          showTeams={needShowTeams(fieldDescription.conditionFieldId)}
        />
      );
    case "link":
      return (
        <LinkEditor
          config={{ inputId, value }}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          editor={fieldDescription.editor!}
          onChange={(input) => onChange(input.value)}
          trigger={candidate.self.trigger}
          actions={scopedActions}
          actionsArePreScoped={actionsArePreScoped}
          loopScope={loopScope}
          action={action}
          integrations={integrations}
          isMultiple={functionConfig[2]}
          isError={action !== undefined && isValueNotValid(candidate, value)}
          disableSplit={true}
        />
      );
    case "phone":
      return (
        <PhoneEditor
          config={{ inputId, value }}
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          editor={fieldDescription.editor!}
          integrations={integrations}
          trigger={candidate.self.trigger}
          onChange={(input) => onChange(input.value)}
          actions={scopedActions}
          actionsArePreScoped={actionsArePreScoped}
          loopScope={loopScope}
          action={action}
          isMultiple={functionConfig[2]}
          isError={action !== undefined && isValueNotValid(candidate, value)}
          disableSplit={true}
        />
      );
    case "number":
      return (
        <NumberEditor
          config={{ inputId, value }}
          onChange={(input) => onChange(input.value)}
          trigger={candidate.self.trigger}
          actions={scopedActions}
          actionsArePreScoped={actionsArePreScoped}
          loopScope={loopScope}
          action={action}
          integrations={integrations}
          allowNegative={false}
          isError={action !== undefined && isValueNotValid(candidate, value)}
          maxValue={
            fieldDescription.editor?.editor.oneofKind === "checklist"
              ? 100
              : undefined
          }
        />
      );
    default:
      return null;
  }
};
